import { Component, OnInit, OnDestroy, forwardRef, Input, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Carrier } from '@shared/models/carrier';
import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { CarrierService } from '@shared/services/carrier.service';
import { BehaviorSubject, Subscription } from 'rxjs';

@Component({
    selector: 'app-carrier-select',
    templateUrl: './carrier-select.component.html',
    styleUrls: ['./carrier-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => CarrierSelectComponent),
        }
    ]
})
export class CarrierSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {

    @Input() set value(val: number) {
        this.carrierId.setValue(val, { emitEvent: false });
    }

    @Input() inputId: string | null = null;
    @Input() readonly: boolean = false;
    @Input() name: string | null = null;
    @Input() tabindex: number | null = null;

    @Input() set disabled(val: boolean) {
        if (val)
            this.carrierId.disable({ emitEvent: false });
        else
            this.carrierId.enable({ emitEvent: false });
    }

    @Output() input = new EventEmitter<number>();

    carrierId = new FormControl<number>(null);

    carriers = new BehaviorSubject<Carrier[]>([]);

    showAddDialog: boolean = false;

    private subscriptions: Subscription[] = [];
    private changeListeners: ((val: number) => void)[] = [];
    private touchListeners: (() => void)[] = [];

    constructor(private carrierService: CarrierService, private activityService: ActivityBlockingService) { }

    ngOnInit(): void {
        this.carrierService.getCarriers()
            .pipe(this.activityService.Wait('Loading Carriers...'))
            .subscribe({
                next: carriers => {
                    this.carriers.next(carriers.slice().sort((l, r) => {
                        const left = l.carrierCode === 'NOSC' || l.carrierCode === 'CUPU' ? '' : l.carrierCodeName;
                        const right = r.carrierCode === 'NOSC' || r.carrierCode === 'CUPU' ? '' : r.carrierCodeName;
                        return left.localeCompare(right);
                    }));
                },
                error: _err => {}
            });

        this.subscriptions = [
            this.carrierId.valueChanges.subscribe(carrierId => {
                this.input.emit(carrierId);
                this.changeListeners.forEach(fn => fn(carrierId));
            })
        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    OnCarrierCreated(newCarrier: Carrier): void {
        this.carriers.next([...this.carriers.value, newCarrier]);

        this.carrierId.setValue(newCarrier.id);
    }

    registerOnTouched(fn: any): void {
        this.touchListeners.push(fn);
    }

    registerOnChange(fn: any): void {
        this.changeListeners.push(fn);
    }

    writeValue(obj: any): void {
        this.carrierId.setValue(obj, { emitEvent: false });
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
